import { Box, ButtonBase, Tooltip } from '@mui/material'
import { keyframes } from '@emotion/react'
import { useRef, useState } from 'react'
import { openMidasPanel } from 'hooks/useMidas'
import kingHead from 'images/midas-king.png'
import {
  LAUNCHER_RIGHT,
  LAUNCHER_SIZE,
  useLauncherBottom
} from './useLauncherBottom'

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'

// Uses the individual scale/translate properties so the finished animation
// never pins `transform`, which hover and press own.
const happyBounce = keyframes`
  0% { scale: 0.6; translate: 0 24px; opacity: 0; }
  55% { scale: 1.06; translate: 0 -6px; opacity: 1; }
  75% { scale: 0.98; translate: 0 1px; }
  100% { scale: 1; translate: 0 0; }
`

// The launcher remounts on every page; only bounce the first time it appears.
let hasBounced = false

export function resetLauncherBounce(): void {
  hasBounced = false
}

export default function MidasLauncher({
  hidden
}: Readonly<{ hidden: boolean }>) {
  const [bounce] = useState(() => {
    const first = !hasBounced
    hasBounced = true
    return first
  })
  const ref = useRef<HTMLButtonElement>(null)
  const bottom = useLauncherBottom(ref)

  return (
    <>
      <Tooltip title='Ask Midas' placement='left'>
        <ButtonBase
          ref={ref}
          aria-label='Ask Midas'
          aria-haspopup='dialog'
          onClick={openMidasPanel}
          disableRipple
          data-testid='midas-launcher'
          // Hidden, not unmounted, while the panel is open so the dialog can
          // hand focus back to it on close.
          style={{ bottom, visibility: hidden ? 'hidden' : 'visible' }}
          sx={{
            position: 'fixed',
            right: LAUNCHER_RIGHT,
            zIndex: theme => theme.zIndex.speedDial,
            width: LAUNCHER_SIZE,
            height: LAUNCHER_SIZE,
            borderRadius: '50%',
            overflow: 'visible',
            transition: 'transform .45s cubic-bezier(.2,.9,.3,1.3)',
            animation: bounce ? `${happyBounce} 700ms ease-out both` : 'none',

            // Just the head. The pink aura behind it (a blurred conic gradient on
            // ::before) painted as a hard SQUARE on iOS Safari, which does not
            // reliably clip a conic background to border-radius once a filter puts
            // the element on its own raster layer — Greg saw the box on a phone,
            // 22 Sep. Rather than chase the rendering bug, the glow is gone: the
            // lift on hover is feedback enough.
            '&:hover, &.Mui-focusVisible': {
              transform: 'translateY(-3px) scale(1.08)'
            },
            '&:active': { transform: 'scale(.96)' },
            '&.Mui-focusVisible': {
              outline: '3px solid',
              outlineColor: 'primary.main',
              outlineOffset: 6
            },

            // Reduced motion: no bounce and no lift.
            [REDUCED_MOTION]: {
              animation: 'none',
              transition: 'none',
              '&:hover, &.Mui-focusVisible, &:active': { transform: 'none' }
            }
          }}
        >
          <Box
            component='img'
            src={kingHead}
            alt=''
            data-testid='midas-launcher-head'
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 8px rgba(120,0,60,.35))',
              pointerEvents: 'none'
            }}
          />
        </ButtonBase>
      </Tooltip>
    </>
  )
}
